/**
 * ImageUploader · v2.6.9 batch BS
 * ================================
 * Drop-in admin CMS image uploader. Renders a preview thumbnail + an upload
 * button + an inline URL input (so admins can paste an external URL OR pick
 * a local file). Uploaded files go to /api/admin/upload which stores them
 * in the `media` collection and returns a /api/media/{id} URL.
 *
 * Props:
 *   - value      current URL string
 *   - onChange   (url: string) => void
 *   - label      field label
 *   - testid     data-testid prefix
 *   - height     preview height (default 192)
 *   - purpose    optional · server-side bucket label
 */
import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Upload, X as XIcon, Image as ImageIcon } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";

export default function ImageUploader({
  value,
  onChange,
  label = "Image",
  testid = "uploader",
  height = 192,
  purpose,
  endpoint = "/admin/upload",
  maxBytes = 100 * 1024 * 1024,
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onFile = async (file) => {
    if (!file) return;
    // v2.6.9 batch CA · Pre-flight 100 MB cap so the user gets instant
    // feedback instead of waiting for the multipart upload to round-trip.
    const MAX_BYTES = maxBytes;
    if (file.size > MAX_BYTES) {
      const mb = (file.size / (1024 * 1024)).toFixed(1);
      const limitMb = Math.round(maxBytes / (1024 * 1024));
      const msg = `File too large (${mb} MB). Max upload size is ${limitMb} MB — please compress and try again.`;
      setErr(msg); toast.error(msg, { duration: 8000 });
      return;
    }
    setBusy(true); setErr("");
    try {
      const form = new FormData();
      form.append("file", file);
      if (purpose) form.append("purpose", purpose);
      const { data } = await adminApi.post(endpoint, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Server returns /api/media/{id}; expand to absolute URL.
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const finalUrl = data.url.startsWith("http") ? data.url : `${base}${data.url}`;
      onChange(finalUrl);
      toast.success("Image uploaded");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Upload failed";
      setErr(msg); toast.error(msg);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div data-testid={`${testid}-block`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">{label}</div>
      <div className="flex items-center gap-2 mb-2">
        <Input
          data-testid={`${testid}-url-input`}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste an image URL or use Upload →"
          className="bg-zinc-950 border-zinc-700 text-zinc-100 flex-1"
        />
        <Button
          type="button"
          data-testid={`${testid}-upload-btn`}
          variant="outline"
          className="bg-zinc-950 border-amber-400/40 text-amber-300 hover:bg-amber-400/10"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : <><Upload className="h-4 w-4 mr-1.5"/>Upload</>}
        </Button>
        {value && (
          <Button
            type="button"
            data-testid={`${testid}-clear-btn`}
            variant="ghost"
            size="icon"
            className="text-zinc-400 hover:text-red-400"
            onClick={() => onChange("")}
            title="Clear image"
          >
            <XIcon className="h-4 w-4"/>
          </Button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
          className="hidden"
          onChange={(e) => onFile(e.target.files?.[0])}
          data-testid={`${testid}-file-input`}
        />
      </div>
      <div
        className="rounded-lg overflow-hidden border-2 border-zinc-800 relative bg-zinc-950 flex items-center justify-center"
        style={{ height }}
        data-testid={`${testid}-preview`}
      >
        {value ? (
          <img
            src={value}
            alt="preview"
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.opacity = 0.2; }}
          />
        ) : (
          <div className="text-zinc-600 text-xs uppercase tracking-widest flex flex-col items-center gap-2">
            <ImageIcon className="h-6 w-6"/>
            No image · Click Upload or paste a URL
          </div>
        )}
      </div>
      {err && <div className="text-xs text-red-400 mt-1.5">{err}</div>}
    </div>
  );
}
